import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { NgClickOutsideDirective, NgClickOutsideExcludeDirective } from 'ng-click-outside2';
import { NgxMasonryModule } from 'ngx-masonry';
import { Subscription } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { ResizeNotifierService } from '../../../resize-observer/resize-notifier.service';
import { RerenderDirective } from '../../../shared/directives/rerender/rerendr.directive';
import { SharedModule } from '../../../shared/shared.module';
import { LoaderComponent } from '../../../ui-elements/loader/loader.component';
import { LoaderService } from '../../../ui-elements/loader/loader.service';
import { BREAKPOINTS } from '../../constants/breakpoints.constants';
import { CatalogueRoutePath, CatalogueRouteType } from '../../enums/route-types.enum';
import { CategoryPathInterface } from '../../models/category-path.model';
import { TimeoutHandle } from '../../types/timeout-handle.type';
import { NavbarElementInterface } from '../navbar-elements';
import { TreeService } from '../tree.service';
import { DetailsComponent } from './details/details.component';
import { ThirdNavbarService } from './third-navbar.service';

export interface ThirdNavbarElement {
  id: number;
  type?: number;
  name: string;
  children: ThirdNavbarElement[];
  path?: string;
  saleCoefficient?: any;
  originalName?: string;
  slug: string;
  isNameModified?: boolean;
  tag?: string;

  parent?: ThirdNavbarElement | null;
  pathId?: string;
}

const ROW_HEIGHT = 12;

@Component({
  selector: 'app-third-navbar',
  templateUrl: './third-navbar.component.html',
  styleUrls: ['./third-navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SharedModule,
    NgClickOutsideDirective,
    NgClickOutsideExcludeDirective,
    LoaderComponent,
    DetailsComponent,
    NgxMasonryModule,
    RouterLink,
    RerenderDirective,
  ],
})
export class ThirdNavbarComponent implements OnInit, OnDestroy {
  resizeObserver = inject(ResizeNotifierService);
  private treeService = inject(TreeService);
  private router = inject(Router);
  private loaderService = inject(LoaderService);
  private thirdNavbarService = inject(ThirdNavbarService);
  private activatedRoute = inject(ActivatedRoute);
  private breakpointObserver = inject(BreakpointObserver);

  @ViewChild('menuItemsLoader', { static: true }) menuItemsLoader: LoaderComponent;

  private preservedQueryParams = ['view'];
  private timer: TimeoutHandle;
  private subscription: Subscription = new Subscription();
  parentElement: NavbarElementInterface;
  elements: ThirdNavbarElement[] = [];
  rowHeight = ROW_HEIGHT;
  highlightedElement: ThirdNavbarElement = null;
  isDetailsBlockHidden: boolean;
  CatalogueRouteType = CatalogueRouteType;
  systemElementsFilter = '';
  currentUrlWithoutParams = '';
  navigationQueryParams: Params = {};
  showBg = false;
  catalogueRoutePaths = CatalogueRoutePath;
  letterIndex = [
    'a',
    'b',
    'c',
    'd',
    'e',
    'f',
    'g',
    'h',
    'i',
    'j',
    'k',
    'l',
    'm',
    'n',
    'o',
    'p',
    'q',
    'r',
    's',
    't',
    'u',
    'v',
    'w',
    'x',
    'y',
    'z',
  ];
  inactiveLetters = [];
  smallScreen = false;
  smallScreenMaxWith = `${parseInt(BREAKPOINTS.XL, 10) - 1}px`;

  private treeData: CategoryPathInterface[] = [];

  public iconToCategoryMap = {
    category__desking: 'ni-cat-desks',
    category__meeting: 'ni-cat-meeting',
    category__storage: 'ni-cat-storage',
    category__executive_furniture: 'ni-cat-executive-furniture',
    category__reception_furniture: 'ni-cat-reception-furniture',
    category__screens__and__acoustic: 'ni-cat-acoustic-solutions',
    category__chairs: 'ni-cat-chairs',
    category__lounge__and__soft_furniture: 'ni-cat-lounge-and-soft-furniture',
    category__cable_management: 'ni-cat-cable-management',
    category__accessories: 'ni-cat-accessories',
    category__marketing_materials: 'ni-cat-marketing-materials',
  };

  ngOnInit() {
    this.subscription.add(
      this.treeService
        .getState()
        .pipe(
          filter(({ second, third }) => !!second && third),
          tap(() => (this.elements = [])),
          switchMap(({ second }) => {
            this.showBg = false;
            this.parentElement = second;

            // show bg for systems because data is not prefetched and request takes time
            if (this.parentElement.id === CatalogueRouteType.SYSTEMS) {
              this.showBg = true;
            }

            this.isDetailsBlockHidden = this.parentElement.id === CatalogueRouteType.COMPONENTS;

            return this.loaderService.load(this.treeService.getCatalogue(this.parentElement.id), this.menuItemsLoader);
          }),
        )
        .subscribe((data) => {
          this.treeData = data;
          const { firstGroupChild } = this.modifyItems(data, this.smallScreen);

          // prevents flickering due to growing elements in third navbar
          setTimeout(() => {
            this.showBg = true;
          }, 0);

          const activeElement = this.router.url.split('/').pop().split('?')[0];
          let activeElementFromRoute = data.find((element) => element.slug === activeElement);

          if (activeElementFromRoute) {
            this.highlightElement(activeElementFromRoute);

            return;
          }

          // try to find child of category that matches current route
          activeElementFromRoute = data.find((category) => category.children.find((child) => child.slug === activeElement));

          if (activeElementFromRoute) {
            const activeChild = activeElementFromRoute.children.find((child) => child.slug === activeElement);
            this.highlightElement(activeChild);

            return;
          }

          this.highlightElement(firstGroupChild);
        }),
    );

    this.currentUrlWithoutParams = this.router.url.split('?')[0];

    this.navigationQueryParams = Object.fromEntries(
      Object.entries(this.activatedRoute.snapshot.queryParams).filter(([key]) => this.preservedQueryParams.includes(key)),
    );

    this.subscription.add(
      this.breakpointObserver.observe(`(max-width: ${this.smallScreenMaxWith})`).subscribe((result) => {
        if (result.matches) {
          this.smallScreen = true;
          this.modifyItems(this.treeData, true);
        } else {
          this.smallScreen = false;
          this.modifyItems(this.treeData);
        }
      }),
    );
  }

  private modifyItems(data: CategoryPathInterface[], smallScreen = false) {
    if (!data.length) {
      return { firstGroup: null, firstGroupChild: null };
    }

    let items: any[];
    switch (this.parentElement.id) {
      case CatalogueRouteType.SYSTEMS:
        items = this.thirdNavbarService.reorderSystems(data);
        items.forEach((el) => (el.children = el.children.sort((a, b) => a.name.localeCompare(b.name, 'en'))));
        break;
      case CatalogueRouteType.CATEGORY:
        items = this.thirdNavbarService.reorderCategories(data, smallScreen);
        break;
      default:
        items = data;
        break;
    }

    if (items.length) {
      this.elements = items;

      if (this.parentElement.id === CatalogueRouteType.SYSTEMS) {
        this.inactiveLetters = this.letterIndex.filter((letter) => !this.elements.find((el) => el?.name[0].toLowerCase() === letter));
      }

      const firstGroup = this.elements.find((parent) => !!parent.children.length);
      const [firstGroupChild] = firstGroup.children;

      return { firstGroup, firstGroupChild };
    }

    return { firstGroup: null, firstGroupChild: null };
  }

  getColumnItems(columnIndex: number, columnCount: number, elements: ThirdNavbarElement[]): ThirdNavbarElement[] {
    const totalElements = elements.length;
    const fullRows = Math.floor(totalElements / columnCount);
    const lastRowElementsCount = totalElements % columnCount;

    const columnSize = columnIndex < lastRowElementsCount ? fullRows + 1 : fullRows;

    let start = 0;

    for (let i = 0; i < columnIndex; i++) {
      start += i < lastRowElementsCount ? fullRows + 1 : fullRows;
    }

    return elements.slice(start, start + columnSize);
  }

  onMouseEnter(subCategory) {
    this.timer = setTimeout(() => {
      this.highlightElement(subCategory);
    }, 50);
  }

  onMouseLeave() {
    clearTimeout(this.timer);
  }

  private highlightElement(element: ThirdNavbarElement) {
    if (this.parentElement.id === CatalogueRouteType.COMPONENTS) {
      this.highlightedElement = null;

      return;
    }

    this.highlightedElement = element;
  }

  onNavbarClose() {
    this.treeService.closeNavbar('third');
  }

  generateIdByCategoryPath(categoryPath: string) {
    return `category__${categoryPath?.toLocaleLowerCase().replace(/ /g, '_').replace(/&/g, '_and_')}`;
  }

  get filteredSystemElements(): ThirdNavbarElement[] {
    return !this.systemElementsFilter.length
      ? this.elements
      : this.elements.filter((element) => element.name.toLowerCase().includes(this.systemElementsFilter.toLowerCase()));
  }

  setFilter(value = '') {
    this.systemElementsFilter = value.toLowerCase();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
